import {uniqueTeams,teamKey} from './team-identities.js';
import './game-story.js';
import './member-home.js';

const reducedMotion=matchMedia('(prefers-reduced-motion:reduce)');

// Each batch occupies the same CSS grid cell. Invisible batches still size that
// cell, so even the longest quote is accounted for before the button is pressed.
function initQuotes(){
  const section=document.querySelector('[data-rotating-quotes]');if(!section)return;
  const original=section.querySelector('.quote-grid');
  const more=section.querySelector('.all-voices');
  const cards=[...section.querySelectorAll('.quote-card')];
  const deck=document.createElement('div');deck.className='quote-pages';deck.id='quote-pages';
  const controls=document.createElement('div');controls.className='quote-controls';
  const status=document.createElement('p');status.setAttribute('role','status');status.setAttribute('aria-live','polite');status.setAttribute('aria-atomic','true');
  const button=document.createElement('button');button.type='button';button.className='button outline';button.setAttribute('aria-controls',deck.id);button.innerHTML='See more <span aria-hidden="true">↻</span>';
  controls.append(status,button);original.replaceWith(deck);more.replaceWith(controls);
  let current=0,batchSize=0,pages=[];
  function show(index){
    current=(index+pages.length)%pages.length;
    pages.forEach((page,i)=>{page.classList.toggle('is-active',i===current);page.inert=i!==current;page.setAttribute('aria-hidden',String(i!==current));});
    status.textContent=`${current*batchSize+1}–${Math.min((current+1)*batchSize,cards.length)} of ${cards.length} voices`;
  }
  function layout(){
    const size=innerWidth<=480?2:innerWidth<=760?4:6;
    if(size===batchSize)return;
    batchSize=size;pages=[];const fragment=document.createDocumentFragment();
    for(let i=0;i<cards.length;i+=size){const page=document.createElement('div');page.className='quote-grid quote-page';page.setAttribute('role','group');page.setAttribute('aria-label',`Player voices ${i+1} to ${Math.min(i+size,cards.length)}`);page.append(...cards.slice(i,i+size));pages.push(page);fragment.append(page);}
    deck.replaceChildren(fragment);show(0);
  }
  button.addEventListener('click',()=>show(current+1));
  layout();section.dataset.quotesReady='true';
  const observer=new ResizeObserver(layout);observer.observe(section);
}

function initTeams(){
  const carousel=document.querySelector('.team-carousel');if(!carousel)return;
  const viewport=carousel.querySelector('.team-carousel-window');
  const track=carousel.querySelector('.team-carousel-track');
  let motionPaused=reducedMotion.matches,hovered=false,focused=false,visible=false;
  let period=0,frame=0,lastTime=0,position=0,manualUntil=0;
  const iconCache=new Map([...track.querySelectorAll('[data-team-key]')].map(a=>[a.dataset.teamKey,{source:a.dataset.iconSource,asset:a.querySelector('img').getAttribute('src')}]));
  function cloneForLoop(){
    track.querySelectorAll('[data-team-clone]').forEach(n=>n.remove());
    const originals=[...track.querySelectorAll('[data-team-key]')];
    const fragment=document.createDocumentFragment();
    for(const a of originals){const clone=a.cloneNode(true);clone.removeAttribute('data-team-key');clone.setAttribute('data-team-clone','');clone.setAttribute('aria-hidden','true');clone.tabIndex=-1;fragment.append(clone);}
    track.append(fragment);
    measure();
  }
  function measure(){
    const first=track.querySelector('[data-team-key]');const duplicate=track.querySelector('[data-team-clone]');
    if(first&&duplicate)period=duplicate.offsetLeft-first.offsetLeft;
    position=viewport.scrollLeft;
  }
  const eligible=()=>visible&&!document.hidden&&!motionPaused&&!hovered&&!focused;
  function tick(now){
    frame=0;
    if(!eligible()){lastTime=0;return;}
    if(now<manualUntil){position=viewport.scrollLeft;lastTime=now;frame=requestAnimationFrame(tick);return;}
    if(lastTime&&period){position+=Math.min(now-lastTime,50)*.03;if(position>=period)position-=period;viewport.scrollLeft=position;}
    lastTime=now;frame=requestAnimationFrame(tick);
  }
  function sync(){
    if(!eligible()){cancelAnimationFrame(frame);frame=0;lastTime=0;}else if(!frame){position=viewport.scrollLeft;frame=requestAnimationFrame(tick);}
  }
  function manual(){manualUntil=performance.now()+5000;position=viewport.scrollLeft;}
  carousel.addEventListener('pointerenter',e=>{if(e.pointerType==='mouse'){hovered=true;sync();}});
  carousel.addEventListener('pointerleave',()=>{hovered=false;sync();});
  carousel.addEventListener('focusin',()=>{focused=true;sync();});
  carousel.addEventListener('focusout',()=>queueMicrotask(()=>{focused=carousel.contains(document.activeElement);sync();}));
  viewport.addEventListener('pointerdown',manual,{passive:true});viewport.addEventListener('wheel',manual,{passive:true});viewport.addEventListener('keydown',manual);
  // Keyboard focus and manual scrolling pause motion; clones are never tab stops.
  viewport.addEventListener('scroll',()=>{if(!eligible()||performance.now()<manualUntil)position=viewport.scrollLeft;},{passive:true});
  document.addEventListener('visibilitychange',sync);
  reducedMotion.addEventListener('change',()=>{motionPaused=reducedMotion.matches;sync();});
  new IntersectionObserver(entries=>{visible=entries[0].isIntersecting;sync();}).observe(carousel);
  new ResizeObserver(measure).observe(viewport);
  cloneForLoop();carousel.dataset.ready='true';sync();
  async function refresh(){
    try{
      const response=await fetch('https://d30gfq2c1vw81b.cloudfront.net/public/teams',{credentials:'omit',signal:AbortSignal.timeout(6500)});if(!response.ok)throw Error('Teams unavailable');
      const data=await response.json();if(!Array.isArray(data.teams))return;
      const teams=uniqueTeams(data.teams);if(!teams.length)return;
      const current=[...track.querySelectorAll('[data-team-key]')];
      const changed=teams.length!==current.length||teams.some((t,i)=>current[i]?.dataset.teamKey!==teamKey(t.name)||current[i]?.dataset.iconSource!==t.icon_url||!current[i]?.href.endsWith(`team_id=${t.id}`));
      if(!changed)return;
      // If someone is using the rail, retain its current contents for this visit.
      if(focused||hovered||performance.now()<manualUntil)return;
      const fragment=document.createDocumentFragment();
      for(const t of teams){
        const a=document.createElement('a');a.className='team-identity';a.dataset.teamKey=teamKey(t.name);a.dataset.iconSource=t.icon_url||'';a.href=`https://lihockey.org/team?team_id=${Number(t.id)}`;
        const img=document.createElement('img');img.width=86;img.height=86;img.alt='';img.loading='lazy';img.decoding='async';
        const cached=iconCache.get(teamKey(t.name));let source='/assets/homepage/crest.webp';
        try{const url=new URL(t.icon_url);if(url.protocol==='https:'&&['lihockey.org','assets.lihockey.org'].includes(url.hostname))source=cached?.source===url.href?cached.asset:url.href;}catch{}
        img.src=source;img.addEventListener('error',()=>{img.src='/assets/homepage/crest.webp';},{once:true});const name=document.createElement('span');name.textContent=t.name;a.append(img,name);fragment.append(a);
      }
      track.replaceChildren(fragment);viewport.scrollLeft=0;cloneForLoop();sync();
    }catch{/* All known teams remain available in the static HTML. */}
  }
  refresh();
}

initQuotes();initTeams();
